// src/components/fiscal/CloneForDevolucaoModal.js
import React, { useState, useEffect } from "react";
import { X, AlertTriangle, Trash2, Send, Loader2, RefreshCw, Building2, User, MapPin } from "lucide-react";
import { fiscalService } from "../../services/fiscalService";
import { pb } from "../../services/pocketbase";

export default function CloneForDevolucaoModal({ isOpen, onClose, invoice, configs, findCatalogItem, onSuccess }) {
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // --- ESTADO DOS DADOS DO DESTINATÁRIO (CLIENTE) ---
  const [destData, setDestData] = useState({
    nome: "",
    cpf_cnpj: "",
    email: "",
    telefone: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    ie: "",
    indicador_ie: "9",
  });

  const [itemsList, setItemsList] = useState([]);
  const [valorFrete, setValorFrete] = useState(0);
  const [valorTotal, setValorTotal] = useState(0);

  // --- SELECIONAR CONFIG PADRÃO ---
  useEffect(() => {
    if (configs && configs.length > 0) {
      // Tenta achar a config que bate com a da nota original
      const origCnpj = invoice?.cnpj_emissor || invoice?.cnpj || invoice?.full_data?.cnpj_emissor || "";
      const match = configs.find(c => c.cnpj.replace(/\D/g, "") === origCnpj.replace(/\D/g, ""));
      if (match) {
        setSelectedConfigId(match.id);
      } else {
        setSelectedConfigId(configs[0].id);
      }
    }
  }, [configs, invoice]);

  // --- PREENCHER DADOS DA NOTA ORIGINAL ---
  useEffect(() => {
    if (isOpen && invoice) {
      const order = invoice.expand?.order?.full_data?.order || invoice.expand?.order || invoice.full_data?.order || {};
      const customer = order.customer || invoice.expand?.order?.customer || invoice.full_data?.customer || {};
      const shipping = order.shipping || invoice.expand?.order?.shipping || invoice.full_data?.shipping || {};
      const address = shipping.address || invoice.expand?.order?.shipping_address || invoice.full_data?.shipping_address || {};

      const fp = invoice.focus_payload || invoice.full_data?.focus_payload || {};

      // Dados Destinatário
      setDestData({
        nome: fp.nome_destinatario || invoice.customer_name || customer.name || "",
        cpf_cnpj: fp.cpf_destinatario || fp.cnpj_destinatario || customer.cpf || customer.cnpj || "",
        email: fp.email_destinatario || customer.email || "",
        telefone: fp.telefone_destinatario || customer.phone || "",
        cep: fp.cep_destinatario || address.zip || "",
        logradouro: fp.logradouro_destinatario || address.street || "",
        numero: fp.numero_destinatario || address.number || "",
        complemento: fp.complemento_destinatario || address.complemento || "",
        bairro: fp.bairro_destinatario || address.bairro || "",
        cidade: fp.municipio_destinatario || address.city || "",
        uf: (fp.uf_destinatario || address.statecode || address.state || "").toUpperCase(),
        ie: fp.inscricao_estadual_destinatario === "ISENTO" ? "" : (fp.inscricao_estadual_destinatario || ""),
        indicador_ie: String(fp.indicador_inscricao_estadual_destinatario || (fp.inscricao_estadual_destinatario === "ISENTO" ? "2" : (fp.inscricao_estadual_destinatario ? "1" : "9"))),
      });

      // Carrega o frete original ou zera se não houver
      const origFrete = parseFloat(fp.valor_frete || shipping.price || 0) || 0;
      setValorFrete(origFrete);

      // Carrega os itens do pedido completo
      const loadOrderItems = async () => {
        const itemUf = (fp.uf_destinatario || address.statecode || address.state || "").toUpperCase().trim();
        const isSp = itemUf === "SP";
        const computedCfop = isSp ? "1202" : "2202";

        try {
          // Tenta ler do focus_payload primeiro (ground truth da nota emitida)
          const originalItems = fp.itens || [];
          if (originalItems.length > 0) {
            const mapped = originalItems.map((item, idx) => {
              const qty = parseFloat(item.quantidade_comercial || 1.0);
              const unitPrice = parseFloat(item.valor_unitario_comercial || 0); // Preserva preco bruto
              const discountVal = parseFloat(item.valor_desconto || 0); // Preserva desconto
              return {
                id: `${invoice.id}_item_${idx}`,
                numero_item: item.numero_item || (idx + 1),
                codigo_produto: item.codigo_produto || "",
                descricao: item.descricao || "",
                quantidade_comercial: qty,
                valor_unitario_comercial: unitPrice,
                valor_bruto: unitPrice * qty,
                valor_desconto: discountVal,
                ncm: item.codigo_ncm || "71131100",
                cfop: computedCfop,
                origem: item.icms_origem || 0,
                csosn: "900",
                pis_cst: "07",
                cofins_cst: "07",
              };
            });
            setItemsList(mapped);
            return;
          }

          let records = [];
          if (invoice.order_number && invoice.order_number !== "AVULSO" && invoice.order_number !== "S/N") {
            records = await pb.collection("production_orders").getFullList({
              filter: `order_number = "${invoice.order_number}" && archived != true`,
            });
          }

          // Fallback se não achar outros itens ou for avulso
          if (records.length === 0) {
            records = [invoice];
          }

          const mapped = records.map((r, idx) => {
            const fullData = r.full_data || {};
            const catalogItem = findCatalogItem ? findCatalogItem(r.sku) : null;
            let price = parseFloat(r.total_value || fullData.valor_unitario_comercial || fullData.price || 0);

            if (price === 0) {
              if (catalogItem && catalogItem.price) {
                price = parseFloat(catalogItem.price);
              } else if (fullData.order?.payment?.total) {
                price = parseFloat(fullData.order.payment.total) / records.length;
              } else if (invoice.full_data?.order?.payment?.total) {
                price = parseFloat(invoice.full_data.order.payment.total) / records.length;
              }
            }

            // Soma os valores adicionais dos specs (ex: "45cm - R$ 45,00")
            const specsObj = r.specs || fullData.specs || {};
            if (specsObj) {
              Object.values(specsObj).forEach(specVal => {
                if (typeof specVal === "string") {
                  const match = specVal.match(/R\$\s*([0-9.,]+)/);
                  if (match) {
                    const valStr = match[1].replace(/\./g, "").replace(",", ".");
                    price += parseFloat(valStr) || 0;
                  }
                }
              });
            }

            return {
              id: r.id,
              numero_item: idx + 1,
              codigo_produto: r.sku || "",
              descricao: fullData.descricao || fullData.specs?.name || r.sku || "Produto Sem Nome",
              quantidade_comercial: 1.0,
              valor_unitario_comercial: price,
              valor_bruto: price,
              ncm: fullData.ncm || catalogItem?.ncm || "71131100",
              cfop: computedCfop,
              origem: 0,
              csosn: "900", // Devolução no Simples Nacional
              pis_cst: "07",
              cofins_cst: "07",
            };
          });

          setItemsList(mapped);
        } catch (err) {
          console.error("Erro ao carregar itens do pedido original:", err);
          // Fallback local simples
          const fullData = invoice.full_data || {};
          const catalogItem = findCatalogItem ? findCatalogItem(invoice.sku || fullData.sku) : null;
          let price = parseFloat(invoice.total_value || fullData.valor_unitario_comercial || 0);
          
          if (price === 0) {
            if (catalogItem && catalogItem.price) {
              price = parseFloat(catalogItem.price);
            } else if (fullData.order?.payment?.total) {
              price = parseFloat(fullData.order.payment.total || 0);
            }
          }

          const specsObj = invoice.specs || fullData.specs || {};
          if (specsObj) {
            Object.values(specsObj).forEach(specVal => {
              if (typeof specVal === "string") {
                const match = specVal.match(/R\$\s*([0-9.,]+)/);
                if (match) {
                  const valStr = match[1].replace(/\./g, "").replace(",", ".");
                  price += parseFloat(valStr) || 0;
                }
              }
            });
          }

          setItemsList([{
            id: invoice.id,
            numero_item: 1,
            codigo_produto: invoice.sku || fullData.sku || "",
            descricao: catalogItem?.name || fullData.descricao || invoice.sku || "Produto Sem Nome",
            quantidade_comercial: 1.0,
            valor_unitario_comercial: price,
            valor_bruto: price,
            ncm: catalogItem?.ncm || fullData.ncm || "71131100",
            cfop: computedCfop,
            origem: 0,
            csosn: "900",
            pis_cst: "07",
            cofins_cst: "07",
          }]);
        }
      };

      loadOrderItems();
    }
  }, [isOpen, invoice, findCatalogItem]);

  // --- CALCULAR CFOP E VALOR TOTAL ---
  useEffect(() => {
    const itemUf = destData.uf.trim().toUpperCase();
    const isSp = itemUf === "SP";
    const computedCfop = isSp ? "1202" : "2202";

    setItemsList((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        cfop: computedCfop,
      }))
    );
  }, [destData.uf]);

  useEffect(() => {
    const total = itemsList.reduce((acc, item) => acc + (item.valor_unitario_comercial * item.quantidade_comercial) - (item.valor_desconto || 0), 0);
    setValorTotal(total + valorFrete);
  }, [itemsList, valorFrete]);

  // --- VALIDAÇÃO ---
  useEffect(() => {
    const errors = [];
    if (!destData.nome.trim()) {
      errors.push("Nome do cliente é obrigatório.");
    } else if (destData.nome.trim().length > 60) {
      errors.push("Nome do cliente não pode exceder 60 caracteres (SEFAZ).");
    }
    
    const cleanCpfCnpj = destData.cpf_cnpj.replace(/\D/g, "");
    if (!cleanCpfCnpj) {
      errors.push("CPF/CNPJ do destinatário é obrigatório.");
    } else if (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14) {
      errors.push("CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos.");
    }

    if (!destData.cep.replace(/\D/g, "")) errors.push("CEP é obrigatório.");
    if (!destData.logradouro.trim()) errors.push("Logradouro é obrigatório.");
    if (!destData.numero.trim()) errors.push("Número é obrigatório.");
    if (!destData.bairro.trim()) errors.push("Bairro é obrigatório.");
    if (!destData.cidade.trim()) errors.push("Cidade é obrigatória.");
    if (destData.uf.trim().length !== 2) errors.push("UF do estado deve conter 2 caracteres.");

    if (itemsList.length === 0) {
      errors.push("A nota precisa ter pelo menos um item para devolução.");
    }

    if (valorFrete < 0) {
      errors.push("O valor do frete não pode ser negativo.");
    }

    itemsList.forEach((item, idx) => {
      if (!item.descricao.trim()) errors.push(`Item #${idx + 1}: Descrição é obrigatória.`);
      if (item.quantidade_comercial <= 0) errors.push(`Item #${idx + 1}: Quantidade deve ser maior que zero.`);
      if (item.valor_unitario_comercial <= 0) errors.push(`Item #${idx + 1}: Valor unitário deve ser maior que zero.`);
      const brutoItem = (item.valor_unitario_comercial || 0) * (item.quantidade_comercial || 0);
      if ((item.valor_desconto || 0) > brutoItem) errors.push(`Item #${idx + 1}: Desconto não pode ser maior que o valor bruto.`);
    });

    setValidationErrors(errors);
  }, [destData, itemsList, valorFrete]);

  if (!isOpen || !invoice) return null;

  const handleRemoveItem = (idx) => {
    setItemsList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx, field, value) => {
    setItemsList((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === "quantidade_comercial" || field === "valor_unitario_comercial") {
        updated[idx].valor_bruto = parseFloat((updated[idx].quantidade_comercial * updated[idx].valor_unitario_comercial).toFixed(2));
      }
      return updated;
    });
  };

  const handleEmitDevolucao = async () => {
    if (validationErrors.length > 0) {
      alert("Por favor, corrija os erros antes de prosseguir.");
      return;
    }

    const config = configs.find(c => c.id === selectedConfigId);
    if (!config) {
      alert("Configuração fiscal não encontrada.");
      return;
    }

    setLoading(true);
    let newRecord = null;
    try {
      const cleanCpfCnpj = destData.cpf_cnpj.replace(/\D/g, "");
      const isCnpj = cleanCpfCnpj.length === 14;
      const originalChave = (invoice.nfe_chave || invoice.full_data?.nfe_chave || "").replace(/\D/g, "");
      const user = pb.authStore.model?.name || pb.authStore.model?.email || "Sistema";

      if (!originalChave) {
        throw new Error("Chave da NFe de origem não encontrada. A devolução precisa referenciar uma NFe válida.");
      }

      const numNota = invoice.nfe_numero || invoice.full_data?.nfe_numero || "";
      const serie = invoice.nfe_serie || invoice.full_data?.nfe_serie || "";
      const refText = numNota ? `\nReferente a NF-e nº ${numNota}${serie ? `, série ${serie}` : ""}` : "";

      // 1. Criar novo registro de devolução em fiscal_invoices
      const devInvoicePayload = {
        order: invoice.order || invoice.full_data?.order || null,
        order_number: `DEV-${invoice.order_number}`,
        customer_name: destData.nome.trim().substring(0, 60),
        cnpj_emissor: config.cnpj.replace(/\D/g, ""),
        ambiente: config.ambiente,
        tipo_nota: "devolucao",
        nfe_status: "processando",
        usuario_emissor: user,
        focus_payload: null, // será preenchido abaixo
        focus_ref: "",
        venda_original: invoice.id,
      };

      newRecord = await pb.collection("fiscal_invoices").create(devInvoicePayload);
      await pb.collection("fiscal_invoices").update(newRecord.id, { focus_ref: newRecord.id });

      // Calcula o rateio do frete proporcional para evitar rejeição da SEFAZ
      let adjustedItems = itemsList.map(item => ({ ...item }));
      let sumItems = adjustedItems.reduce((acc, item) => acc + (item.valor_unitario_comercial * item.quantidade_comercial), 0);

      let freightPerItem = [];
      if (valorFrete > 0 && sumItems > 0) {
        let remainingFreight = valorFrete;
        freightPerItem = adjustedItems.map((item, idx) => {
          if (idx === adjustedItems.length - 1) {
            return parseFloat(remainingFreight.toFixed(2));
          }
          const itemShare = (item.valor_unitario_comercial * item.quantidade_comercial) / sumItems;
          const itemFreight = parseFloat((valorFrete * itemShare).toFixed(2));
          remainingFreight -= itemFreight;
          return itemFreight;
        });
      }

      // 2. Montar Focus Payload de entrada e devolução
      const focus_payload = {
        cnpj_emitente: config.cnpj.replace(/\D/g, ""),
        inscricao_estadual_emitente: config.inscricao_estadual.replace(/\D/g, ""),
        regime_tributario_emitente: 1, // Simples Nacional
        data_emissao: new Date().toISOString(),
        tipo_documento: 0, // Entrada
        finalidade_emissao: 4, // Devolução
        consumidor_final: 1,
        presenca_comprador: 2,
        natureza_operacao: "Devolução de venda de mercadoria",
        modalidade_frete: 9, // Sem frete
        informacoes_adicionais_contribuinte: "DOCUMENTO EMITIDO POR ME OU EPP OPTANTE PELO SIMPLES NACIONAL\nNÃO GERA DIREITO A CRÉDITO FISCAL" + refText,

        nome_destinatario: destData.nome.trim().substring(0, 60),
        [isCnpj ? "cnpj_destinatario" : "cpf_destinatario"]: cleanCpfCnpj,
        ...(isCnpj ? {
          indicador_inscricao_estadual_destinatario: parseInt(destData.indicador_ie || "9"),
          inscricao_estadual_destinatario: destData.indicador_ie === "1" ? (destData.ie || "").replace(/\D/g, "") : (destData.indicador_ie === "2" ? "ISENTO" : undefined),
        } : {
          indicador_inscricao_estadual_destinatario: 9,
          inscricao_estadual_destinatario: "",
        }),
        email_destinatario: destData.email.trim() || undefined,
        telefone_destinatario: destData.telefone.replace(/\D/g, "") || undefined,

        logradouro_destinatario: destData.logradouro.trim(),
        numero_destinatario: destData.numero.trim(),
        complemento_destinatario: destData.complemento.trim() || undefined,
        bairro_destinatario: destData.bairro.trim(),
        municipio_destinatario: destData.cidade.trim(),
        uf_destinatario: destData.uf.trim().toUpperCase(),
        cep_destinatario: destData.cep.replace(/\D/g, ""),

        // SEFAZ exige notas referenciadas na devolução
        notas_referenciadas: [
          {
            chave_nfe: originalChave
          }
        ],

        itens: adjustedItems.map((item, idx) => {
          const itemFreight = freightPerItem[idx] || 0;
          return {
            numero_item: item.numero_item,
            codigo_produto: item.codigo_produto,
            descricao: item.descricao,
            unidade_comercial: "UN",
            quantidade_comercial: item.quantidade_comercial,
            valor_unitario_comercial: item.valor_unitario_comercial,
            valor_bruto: item.valor_bruto,
            valor_desconto: item.valor_desconto > 0 ? item.valor_desconto : undefined,
            valor_frete: itemFreight > 0 ? itemFreight : undefined,
            unidade_tributavel: "UN",
            quantidade_tributavel: item.quantidade_comercial,
            valor_unitario_tributavel: item.valor_unitario_comercial,
            codigo_ncm: item.ncm,
            cfop: item.cfop,
            icms_origem: item.origem,
            icms_situacao_tributaria: "900", // Devolução Simples Nacional
            pis_situacao_tributaria: "07",
            cofins_situacao_tributaria: "07"
          };
        }),

        valor_desconto: adjustedItems.reduce((acc, i) => acc + (i.valor_desconto || 0), 0) > 0
          ? parseFloat(adjustedItems.reduce((acc, i) => acc + (i.valor_desconto || 0), 0).toFixed(2))
          : undefined,
        valor_frete: valorFrete > 0 ? valorFrete : undefined,
        valor_total: valorTotal,
      };

      // Atualiza o payload e foco no registro de nota fiscal recém-criado
      await pb.collection("fiscal_invoices").update(newRecord.id, {
        focus_payload: focus_payload,
      });

      // 3. Monta payload do n8n
      const token = (config.ambiente === "producao" ? config.token_producao : config.token_homologacao || "").trim();
      const n8n_payload = {
        ref: newRecord.id, // O ID real direto
        orderNumber: `DEV-${invoice.order_number}`,
        cnpj_emissor: config.cnpj,
        ambiente: config.ambiente,
        token: token,
        pb_token: pb.authStore.token,
        items: [{ id: newRecord.id }],
        focus_payload: focus_payload,
      };

      // 4. Salva o vínculo no pedido/nota original
      const origFullData = {
        ...(invoice.full_data || {}),
        devolucao_id: newRecord.id,
      };
      await pb.collection("fiscal_invoices").update(invoice.id, {
        full_data: origFullData
      });

      // 5. Dispara emissão no n8n
      await fiscalService.requestInvoice(n8n_payload);

      alert("Emissão de nota fiscal de devolução iniciada com sucesso!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      let errMsg = err.message;
      if (err.data && err.data.data) {
        const details = Object.entries(err.data.data)
          .map(([key, val]) => `${key}: ${val.message || JSON.stringify(val)}`)
          .join(", ");
        errMsg += ` (Detalhes: ${details})`;
      }

      // Se o novo registro foi criado, marca-o como erro para liberar a tela
      if (newRecord && newRecord.id) {
        try {
          await pb.collection("fiscal_invoices").update(newRecord.id, {
            nfe_status: "erro",
            nfe_error_message: errMsg
          });
          if (onSuccess) onSuccess();
        } catch (updateErr) {
          console.error("Erro ao atualizar nota para status erro no PocketBase:", updateErr);
        }
      }

      alert("Erro ao emitir devolução: " + errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col my-8 max-h-[90vh]">
        {/* Header */}
        <div className="bg-orange-600 p-4 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <RefreshCw size={24} className="animate-spin-slow" />
            </div>
            <div>
              <h2 className="font-extrabold text-lg leading-tight">
                Clonar para Devolução (NF-e de Entrada)
              </h2>
              <p className="text-orange-100 text-xs">
                Ref. Venda #{invoice.order_number} • Referenciando chave Focus
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 custom-scrollbar">
          {/* Alerta de Entrada Fiscal */}
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3 text-orange-850">
            <AlertTriangle className="text-orange-600 shrink-0 mt-0.5" size={18} />
            <div className="text-xs">
              <h4 className="font-bold text-orange-900 uppercase mb-0.5">Aviso Importante (Nota de Entrada)</h4>
              <p>
                Esta operação criará uma **NF-e de entrada** com finalidade de **Devolução (finalidade = 4)** para anular a venda e recuperar os impostos sobre as joias devolvidas pelo cliente. A chave da nota original será referenciada automaticamente.
              </p>
            </div>
          </div>

          {/* Seleção do Emissor (Travada) */}
          <div className="border rounded-xl p-4 bg-slate-50 border-slate-200">
            <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
              <Building2 size={14} /> Empresa Emitente da Devolução
            </h3>
            {(() => {
              const selectedConfig = configs.find(c => c.id === selectedConfigId);
              return selectedConfig ? (
                <div className="flex p-3 border rounded-xl border-orange-200 bg-orange-50/15 items-center gap-3 max-w-sm">
                  <Building2 className="text-orange-600 shrink-0" size={18} />
                  <div>
                    <div className="font-bold text-slate-800 text-xs leading-tight">{selectedConfig.razao_social}</div>
                    <div className="text-[10px] text-slate-450 font-mono mt-0.5">{selectedConfig.cnpj}</div>
                    <span className={`inline-block text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full mt-1 border ${
                      selectedConfig.ambiente === "producao" ? "bg-orange-50 text-orange-600 border-orange-100" : "bg-blue-50 text-blue-600 border-blue-100"
                    }`}>
                      {selectedConfig.ambiente}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="text-slate-450 text-xs">Nenhum emitente correspondente encontrado.</span>
              );
            })()}
          </div>

          {/* Erros de Validação */}
          {validationErrors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
              <div>
                <h4 className="font-bold text-red-800 text-xs uppercase">Campos obrigatórios pendentes</h4>
                <ul className="text-xs text-red-700 list-disc list-inside mt-1 space-y-0.5">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Dados do Cliente */}
          <div className="border rounded-xl p-4 bg-white border-slate-200">
            <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
              <User size={14} /> Dados do Remetente (Cliente Devolvendo)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Nome Completo</label>
                  {destData.nome && destData.nome.length >= 50 && (
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${destData.nome.length >= 60 ? "text-red-500 animate-pulse" : "text-amber-500"}`}>
                      {destData.nome.length}/60 Caracteres
                    </span>
                  )}
                </div>
                <input
                  className={`w-full p-2 border rounded text-xs outline-none transition-colors ${destData.nome && destData.nome.length >= 60 ? "border-red-300 focus:border-red-500 font-medium text-red-900 bg-red-50/10" : "focus:border-orange-500 font-medium"}`}
                  value={destData.nome}
                  maxLength="60"
                  onChange={(e) => setDestData(prev => ({ ...prev, nome: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">CPF ou CNPJ</label>
                <input
                  className="w-full p-2 border rounded text-xs outline-none focus:border-orange-500 font-mono font-medium"
                  value={destData.cpf_cnpj}
                  onChange={(e) => setDestData(prev => ({ ...prev, cpf_cnpj: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">UF (Estado)</label>
                <input
                  className="w-full p-2 border rounded text-xs font-bold text-center uppercase focus:border-orange-500 outline-none"
                  maxLength={2}
                  value={destData.uf}
                  onChange={(e) => setDestData(prev => ({ ...prev, uf: e.target.value.toUpperCase() }))}
                />
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className="border rounded-xl p-4 bg-white border-slate-200">
            <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
              <MapPin size={14} /> Endereço do Cliente
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">CEP</label>
                <input
                  className="w-full p-2 border rounded text-xs font-mono font-medium"
                  value={destData.cep}
                  onChange={(e) => setDestData(prev => ({ ...prev, cep: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Rua / Logradouro</label>
                <input
                  className="w-full p-2 border rounded text-xs font-medium"
                  value={destData.logradouro}
                  onChange={(e) => setDestData(prev => ({ ...prev, logradouro: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Número</label>
                <input
                  className="w-full p-2 border rounded text-xs font-medium"
                  value={destData.numero}
                  onChange={(e) => setDestData(prev => ({ ...prev, numero: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Bairro</label>
                <input
                  className="w-full p-2 border rounded text-xs font-medium"
                  value={destData.bairro}
                  onChange={(e) => setDestData(prev => ({ ...prev, bairro: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Cidade</label>
                <input
                  className="w-full p-2 border rounded text-xs font-medium"
                  value={destData.cidade}
                  onChange={(e) => setDestData(prev => ({ ...prev, cidade: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Notas Referenciadas */}
          <div className="border rounded-xl p-4 bg-slate-50 border-slate-200">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block mb-1">
              Chave de Acesso Referenciada (NFe Origem)
            </label>
            <input
              className="w-full p-2.5 border rounded-lg text-xs font-mono font-bold bg-slate-100 text-slate-600"
              readOnly
              value={invoice.nfe_chave || invoice.full_data?.nfe_chave || "Não Identificada"}
            />
          </div>

          {/* Itens para Devolução */}
          <div className="border rounded-xl p-4 bg-white border-slate-200">
            <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
              Itens Fiscais Retornados
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b text-slate-400 uppercase">
                    <th className="py-2 pr-2">SKU</th>
                    <th className="py-2 pr-2">Descrição</th>
                    <th className="py-2 pr-2 text-right w-20">Qtd</th>
                    <th className="py-2 pr-2 text-right w-28">Preço Unit</th>
                    <th className="py-2 pr-2 text-right w-28">Desconto</th>
                    <th className="py-2 pr-2 text-right w-28">Subtotal</th>
                    <th className="py-2 text-center w-24">CFOP</th>
                    <th className="py-2 text-center w-24">CSOSN</th>
                    <th className="py-2 text-center w-12">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsList.map((item, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="py-2.5 font-mono font-bold text-orange-600">{item.codigo_produto}</td>
                      <td className="py-2.5 pr-2">
                        <input
                          className="w-full p-1 border rounded text-xs font-medium"
                          value={item.descricao}
                          onChange={(e) => handleItemChange(idx, "descricao", e.target.value)}
                        />
                      </td>
                      <td className="py-2.5 pr-2">
                        <input
                          type="number"
                          step="1"
                          className="w-16 p-1 border rounded text-center text-xs font-mono font-medium"
                          value={item.quantidade_comercial}
                          onChange={(e) => handleItemChange(idx, "quantidade_comercial", parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="py-2.5 pr-2">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-slate-400">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            className="w-20 p-1 border rounded text-right text-xs font-mono font-medium"
                            value={item.valor_unitario_comercial}
                            onChange={(e) => handleItemChange(idx, "valor_unitario_comercial", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </td>
                      <td className="py-2.5 pr-2">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-slate-400">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-20 p-1 border rounded text-right text-xs font-mono font-medium"
                            value={item.valor_desconto || 0}
                            onChange={(e) => handleItemChange(idx, "valor_desconto", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </td>
                      <td className="py-2.5 pr-2 text-right font-mono font-bold text-slate-700">
                        R$ {(((item.valor_unitario_comercial || 0) * (item.quantidade_comercial || 0)) - (item.valor_desconto || 0)).toFixed(2)}
                      </td>
                      <td className="py-2.5 text-center font-mono font-bold text-slate-700">{item.cfop || "---"}</td>
                      <td className="py-2.5 text-center font-mono text-slate-500">{item.csosn}</td>
                      <td className="py-2.5 text-center">
                        <button
                          onClick={() => handleRemoveItem(idx)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remover Item da Devolução"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end items-center gap-6 mt-4 pt-3 border-t border-slate-100 text-xs">
              <div className="flex items-center gap-2 font-bold text-slate-700">
                <span>Valor do Frete:</span>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-24 p-1 border rounded text-right text-xs font-mono font-medium focus:border-orange-500 outline-none"
                    value={valorFrete}
                    onChange={(e) => setValorFrete(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                Total Devolução:
                <span className="font-mono text-orange-600 text-base">R$ {valorTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-3 bg-slate-50 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-500 font-bold hover:bg-slate-200 rounded-lg text-sm"
          >
            Fechar
          </button>
          <button
            onClick={handleEmitDevolucao}
            disabled={loading || validationErrors.length > 0}
            className="px-6 py-2 bg-orange-600 text-white font-extrabold rounded-lg hover:bg-orange-700 shadow-lg flex items-center gap-2 text-sm disabled:bg-orange-300 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processando Emissão...
              </>
            ) : (
              <>
                <Send size={16} />
                Confirmar & Emitir Devolução
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
